import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  UserCircleIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

import Avatar from "../../Avatar";
import { capitalizeWords } from "../../../utils";
import { routePaths } from "../../../constants";
import { User } from "../../../services/queries/users/types";
import { Business } from "../../../services/queries/businesses/types";

interface UserMenuProps {
  user: User | null;
  business: Business | null;
  onSignOut: () => void;
}

export default function UserMenu({ user, business, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const initials = `${user?.firstName?.[0] ?? ""}${
    user?.lastName?.[0] ?? ""
  }`.toUpperCase();

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        close();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  // Focus first item on open; keyboard navigation
  useEffect(() => {
    if (!open) return;

    const items = () =>
      Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
          [],
      );
    items()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      const menuItems = items();
      const activeIndex = menuItems.indexOf(
        document.activeElement as HTMLElement,
      );

      switch (event.key) {
        case "Escape":
          event.preventDefault();
          close(true);
          break;
        case "ArrowDown":
          event.preventDefault();
          menuItems[(activeIndex + 1) % menuItems.length]?.focus();
          break;
        case "ArrowUp":
          event.preventDefault();
          menuItems[
            (activeIndex - 1 + menuItems.length) % menuItems.length
          ]?.focus();
          break;
        case "Home":
          event.preventDefault();
          menuItems[0]?.focus();
          break;
        case "End":
          event.preventDefault();
          menuItems[menuItems.length - 1]?.focus();
          break;
        case "Tab":
          close();
          break;
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <Avatar
          ref={triggerRef}
          characters={initials || "?"}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls="user-menu"
          aria-label="Open user menu"
        />
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-500 cursor-pointer transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
          onClick={() => setOpen((prev) => !prev)}
        />
      </div>

      {open && (
        <div
          ref={menuRef}
          id="user-menu"
          role="menu"
          aria-label="User menu"
          className="absolute right-0 top-full mt-2 w-64 rounded-lg bg-white shadow-dropdown border border-gray-100 py-2 z-50"
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="font-medium text-gray-900 truncate">
              {capitalizeWords(user?.firstName ?? "")}{" "}
              {capitalizeWords(user?.lastName ?? "")}
            </p>
            <p className="text-sm text-gray-500 truncate">{user?.email}</p>
            {business?.name && (
              <p className="text-xs text-gray-400 truncate mt-0.5">
                {capitalizeWords(business.name)}
              </p>
            )}
          </div>

          <button
            role="menuitem"
            tabIndex={-1}
            className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
            onClick={() => {
              close();
              navigate({ to: routePaths.profile });
            }}
          >
            <UserCircleIcon
              className="h-5 w-5 mr-3 text-gray-400"
              aria-hidden="true"
            />
            View Profile
          </button>

          <button
            role="menuitem"
            tabIndex={-1}
            className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 focus:bg-red-50 focus:outline-none"
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            <ArrowRightStartOnRectangleIcon
              className="h-5 w-5 mr-3"
              aria-hidden="true"
            />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
